import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import {
  LayoutDashboard, GitBranch, Briefcase, Users, Server, Wrench, DollarSign,
  BarChart3, Settings, Search, Bell, LogOut, ChevronRight,
  CreditCard, PanelLeftClose, PanelLeftOpen, CalendarClock, TrendingUp,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/pipeline", label: "Pipeline", icon: GitBranch },
  { to: "/projects", label: "Projects", icon: Briefcase },
  { to: "/clients", label: "Clients", icon: Users },
  { to: "/billing", label: "Billing", icon: CreditCard },
  { to: "/renewals", label: "Renewals", icon: CalendarClock },
  { to: "/profitability", label: "Profitability", icon: TrendingUp },
  { to: "/hosting", label: "Hosting", icon: Server },
  { to: "/maintenance", label: "Maintenance", icon: Wrench },
  { to: "/finance", label: "Finance", icon: DollarSign },
  { to: "/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/settings", label: "Settings", icon: Settings },
];

function Sidebar({ collapsed, toggle }) {
  return (
    <aside
      className={`fixed left-0 top-0 h-screen z-40 flex flex-col transition-all duration-300 ${collapsed ? "w-[68px]" : "w-64"}`}
      style={{
        background: "rgba(8,8,10,0.92)",
        backdropFilter: "blur(28px)",
        WebkitBackdropFilter: "blur(28px)",
        borderRight: "1px solid rgba(255,255,255,0.06)",
      }}
      data-testid="sidebar"
    >
      <div className={`px-4 pt-6 pb-5 flex items-center ${collapsed ? "justify-center" : "justify-between"}`}>
        {!collapsed && (
          <div className="flex items-center gap-3">
            <img
              src="/equinox-logo-128.png"
              alt="Equinox"
              className="w-10 h-10 object-contain drop-shadow-[0_0_18px_rgba(0,122,255,0.5)]"
              data-testid="sidebar-logo"
            />
            <div>
              <div className="font-display text-lg font-medium tracking-tight text-white leading-none">Equinox</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mt-1">Operating System</div>
            </div>
          </div>
        )}
        {collapsed && (
          <img
            src="/equinox-logo-128.png"
            alt="Equinox"
            className="w-9 h-9 object-contain drop-shadow-[0_0_16px_rgba(0,122,255,0.5)]"
            data-testid="sidebar-logo-collapsed"
          />
        )}
        <button
          onClick={toggle}
          data-testid="sidebar-toggle"
          className={`w-8 h-8 rounded-xl bg-white/[0.04] hover:bg-white/[0.10] flex items-center justify-center text-white/55 hover:text-white transition ${collapsed ? "mt-3" : ""}`}
          title={collapsed ? "Expand" : "Collapse"}
        >
          {collapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
        </button>
      </div>

      <nav className={`flex-1 ${collapsed ? "px-2" : "px-3"} space-y-1`}>
        {NAV.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            data-testid={`nav-${label.toLowerCase()}`}
            title={collapsed ? label : undefined}
            className={({ isActive }) =>
              `group flex items-center gap-3 ${collapsed ? "justify-center px-2" : "px-4"} py-3 rounded-2xl transition-all ${
                isActive ? "bg-white/[0.06] text-white" : "text-white/55 hover:text-white hover:bg-white/[0.03]"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <Icon className={`w-4 h-4 ${isActive ? "text-[#007AFF]" : ""}`} />
                {!collapsed && <span className="text-sm font-medium tracking-tight">{label}</span>}
                {!collapsed && isActive && <ChevronRight className="w-3.5 h-3.5 ml-auto text-[#007AFF]" />}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {!collapsed && (
        <div className="px-6 py-5 border-t border-white/[0.06]">
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">Equinox Systems</div>
          <div className="text-xs text-white/50 mt-1">v1.1 · Admin</div>
        </div>
      )}
    </aside>
  );
}

function GlobalSearchBar({ onJump }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!q || q.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search`, { params: { q } });
        setResults(data.results || []);
        setOpen(true);
      } catch (_e) { /* ignore */ }
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  const handleJump = (r) => {
    setOpen(false);
    setQ("");
    onJump(r);
  };

  return (
    <div className="relative w-full max-w-lg">
      <div className="flex items-center gap-3 px-4 py-2.5 rounded-2xl bg-white/[0.04] border border-white/[0.08] backdrop-blur-2xl focus-within:border-[#007AFF]/40">
        <Search className="w-4 h-4 text-white/40" />
        <input
          data-testid="global-search-input"
          value={q}
          onFocus={() => results.length && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search clients, projects, domains…"
          className="bg-transparent outline-none flex-1 text-sm text-white placeholder:text-white/30"
        />
        <kbd className="text-[10px] text-white/30 border border-white/10 rounded px-1.5 py-0.5">⌘K</kbd>
      </div>
      {open && results.length > 0 && (
        <div
          data-testid="global-search-results"
          className="absolute top-full left-0 right-0 mt-2 rounded-2xl overflow-hidden shadow-2xl max-h-96 overflow-y-auto z-50"
          style={{ background: "rgba(14,14,18,0.96)", border: "1px solid rgba(255,255,255,0.12)", backdropFilter: "blur(32px)" }}
        >
          {results.map((r, i) => (
            <button
              key={i}
              onMouseDown={() => handleJump(r)}
              className="w-full text-left px-4 py-3 hover:bg-white/[0.06] flex items-center justify-between gap-4"
              data-testid={`search-result-${r.type}-${i}`}
            >
              <div>
                <div className="text-sm text-white">{r.title}</div>
                <div className="text-xs text-white/40">{r.subtitle}</div>
              </div>
              <div className="text-[10px] uppercase tracking-widest text-[#007AFF]">{r.type}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function NotificationsBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/notifications");
        setItems(data.items || []);
      } catch (_e) { /* ignore */ }
    })();
  }, []);

  const sevColor = {
    error: "bg-rose-400",
    warning: "bg-amber-400",
    info: "bg-sky-400",
  };

  return (
    <div className="relative">
      <button
        data-testid="notifications-bell"
        onClick={() => setOpen((v) => !v)}
        className="relative w-10 h-10 rounded-full bg-white/[0.04] border border-white/[0.08] flex items-center justify-center hover:bg-white/[0.08] transition"
      >
        <Bell className="w-4 h-4 text-white/70" />
        {items.length > 0 && (
          <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-[#007AFF] animate-pulse-glow" />
        )}
      </button>
      {open && (
        <div
          data-testid="notifications-panel"
          className="absolute right-0 top-full mt-2 w-96 rounded-2xl shadow-2xl overflow-hidden z-50"
          style={{
            background: "rgba(14,14,18,0.96)",
            border: "1px solid rgba(255,255,255,0.12)",
            backdropFilter: "blur(32px)",
            WebkitBackdropFilter: "blur(32px)",
          }}
        >
          <div className="px-5 py-4 border-b border-white/[0.06] bg-white/[0.02]">
            <div className="text-sm font-medium">Notifications</div>
            <div className="text-xs text-white/40">{items.length} active</div>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 && (
              <div className="px-5 py-8 text-center text-sm text-white/40">You&apos;re all caught up</div>
            )}
            {items.map((it, i) => (
              <div key={i} className="px-5 py-3 border-b border-white/[0.04] hover:bg-white/[0.04] flex items-start gap-3 transition">
                <div className={`w-1.5 h-1.5 rounded-full mt-2 ${sevColor[it.severity] || "bg-white/40"}`} />
                <div className="flex-1">
                  <div className="text-sm text-white">{it.title}</div>
                  <div className="text-xs text-white/45">{it.subtitle}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("equinox_sidebar_collapsed") === "1"; } catch { return false; }
  });

  const toggle = () => {
    setCollapsed((v) => {
      const next = !v;
      try { localStorage.setItem("equinox_sidebar_collapsed", next ? "1" : "0"); } catch {}
      return next;
    });
  };

  const onJump = (r) => {
    const map = { client: "/clients", project: "/projects", pipeline: "/pipeline", hosting: "/hosting", domain: "/hosting" };
    nav(map[r.type] || "/");
  };

  const leftPad = collapsed ? "ml-[68px]" : "ml-64";
  const headerLeft = collapsed ? "left-[68px]" : "left-64";

  return (
    <div className="equinox-bg min-h-screen relative">
      <Sidebar collapsed={collapsed} toggle={toggle} />
      <header
        className={`fixed top-0 ${headerLeft} right-0 z-30 h-16 flex items-center px-8 gap-6 transition-all duration-300`}
        style={{
          background: "rgba(5,5,5,0.85)",
          backdropFilter: "blur(28px)",
          WebkitBackdropFilter: "blur(28px)",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
        }}
        data-testid="topbar"
      >
        <GlobalSearchBar onJump={onJump} />
        <div className="ml-auto flex items-center gap-3">
          <NotificationsBell />
          <div className="flex items-center gap-3 pl-3 pr-4 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.08]">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#007AFF] to-[#0050C8] flex items-center justify-center text-[11px] font-semibold">
              {(user?.name || "A").slice(0, 1)}
            </div>
            <div className="hidden sm:block">
              <div className="text-xs font-medium leading-tight">{user?.name || "Admin"}</div>
              <div className="text-[10px] text-white/40 leading-tight">{user?.role || "admin"}</div>
            </div>
            <button data-testid="logout-btn" onClick={logout} className="ml-2 text-white/40 hover:text-white" title="Logout">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <main className={`${leftPad} pt-16 relative z-10 transition-all duration-300`}>
        <div className="px-8 py-8">{children}</div>
      </main>
    </div>
  );
}
